import * as angular from 'angular';
import * as d3 from 'd3';

import { ComponentDefinition, ITreeItem, State, IGroupValuesData, IQualityOverviewMerged, RefreshClickData } from '../../../models';
import { IGroupService, IIdentifierValuesStatusService, IDateRangeService, IStateService, IExportSvgService, IExportToCsvService, IQualityAnalysisHelper } from '../../../services';
import { refreshClick } from '../../../common/';


class OverviewQualityStateController implements angular.IComponentController {
    isLoading: boolean;
    loadingMessage: string;
    groups: ITreeItem[];
    displayedGroups: ITreeItem[];
    data: IGroupValuesData;
    states: State[];
    beginDate: string;
    endDate: string;
    merged: IQualityOverviewMerged[];
    private _watchers: any[];
    static $inject = [
        '$scope',
        '$element',
        '$state',
        'groupService',
        'identifierValuesStatusService',
        'dateRangeService',
        'stateService',
        'exportSvgService',
        'exportToCsvService',
        'qualityAnalysisHelper'
    ];

    constructor(private _$scope: angular.IScope,
        private _$element: angular.IAugmentedJQuery,
        private _$state: angular.ui.IStateService,
        private _groupService: IGroupService,
        private _identifierValuesStatusService: IIdentifierValuesStatusService,
        private _dateRangeService: IDateRangeService,
        private _stateService: IStateService,
        private _exportSvgService: IExportSvgService,
        private _exportToCsvService: IExportToCsvService,
        private _qualityAnalysisHelper: IQualityAnalysisHelper) {
    }

    $onInit() {
        this.loadingMessage = 'Loading states';
        this.isLoading = true;
        this._watchers = [];
        this._stateService.getAll()
            .then(data => {
                this.states = data.map(d => d.state);
                // TODO : should be sync with overview, let like this for now
                this.loadingMessage = 'Loading groups';
                return this._groupService.getAllAsTree();
            })
            .then(data => {
                this.groups = data.slice(1, data.length);
                this.loadingMessage = 'Loading data';
                return this.refresh(this._dateRangeService.beginDate() as string, this._dateRangeService.endDate() as string, false);

            })
            .then(() => {
                this._$scope.$on(refreshClick, (event, data: RefreshClickData) => {
                    this.beginDate = data.beginDate;
                    this.endDate = data.endDate;
                    if (this.displayedGroups) {
                        this.refresh(data.beginDate, data.endDate, data.force);
                    }
                });
                this._watchers.push(this._$scope.$watch(() => this.displayedGroups, () => {
                    this.refresh(this._dateRangeService.beginDate() as string, this._dateRangeService.endDate() as string, false);
                }));
            }).finally(() => {
                this.isLoading = false;
            });

    }

    $onDestroy(): void {
        this._watchers.forEach(w => w());
    }

    private refresh(beginDate: string, endDate: string, force: boolean) {
        this.loadingMessage = 'Loading data';
        this.isLoading = true;
        return this._identifierValuesStatusService.getOverview(beginDate, endDate, force)
            .then(data => {
                this.data = data;
                this.merged = this._qualityAnalysisHelper.mergeData(
                    this.displayedGroups, this.data, this.states,
                    (state, params) => {
                        return () => {
                            try {
                                if (params.selectedGroup) {
                                    const c = angular.copy(params.selectedGroup);
                                    c.parent = null;
                                    params.selectedGroup = c;
                                }
                            } catch (e) {
                                console.error(e);
                            }
                            this._$state.go(state, params);
                        };
                    });
            }).finally(() => {
                this.isLoading = false;
            });
    }

    exportToPng() {
        const svg: SVGSVGElement = d3.select(this._$element[0]).select('svg')
            .node() as any;
        this.isLoading = true;
        this.loadingMessage = 'Exporting to PNG';
        this._exportSvgService.exportSvgAsPng('Overall quality', svg)
            .finally(() => this.isLoading = false);
    }

    exportToSvg() {
        const svg: SVGSVGElement = d3.select(this._$element[0]).select('svg')
            .node() as any;
        this.isLoading = true;
        this.loadingMessage = 'Exporting to SVG';
        this._exportSvgService.exportSvg('Overall quality', svg)
            .finally(() => this.isLoading = false);
    }

    exportToCsv() {
        const data: string[][] = [];

        data.push(['Group name', 'Measured', '', '', '', 'Calculated', '', '', '', 'Validated', '', '', '']);
        data.push(['', 'OK', 'Warning', 'Error', 'Missing', 'OK', 'Warning', 'Error', 'Missing', 'OK', 'Warning', 'Error', 'Missing']);

        // Prepare states
        const statesDictionnary = {};
        this.merged.forEach(r => r.data[3].data.forEach(s => statesDictionnary[s.name] = true));
        const states = Object.keys(statesDictionnary);
        const titleIndex = data[0].length;
        if (states.length > 0) {
            states.forEach(s => {
                data[0].push('');
                data[1].push(s);
            });
            data[0][titleIndex] = 'States';
        }
        const maxIndex = data[1].length;

        // Set values
        this.merged.forEach(r => {
            const toAdd = [(r.group as any).uniqueName];
            for (let j = 0; j < 3; j++) {
                for (let i = 0; i < 4; i++) {
                    const v = r.data[j].data[i];
                    toAdd.push(v ? v.value : '');
                }
            }
            // complete for variable states column
            while (toAdd.length < maxIndex) {
                toAdd.push('');
            }

            // states values
            r.data[3].data.forEach(s => {
                const delta = states.indexOf(s.name);
                toAdd[delta + titleIndex] = s.value;
            });

            data.push(toAdd);
        });
        this._exportToCsvService.getCsvFromData(data, 'Overall quality overview.csv');
    }
}

export const wtOverviewQualityState: ComponentDefinition = {
    componentId: 'wtOverviewQualityState',
    controller: OverviewQualityStateController,
    template: require('./overviewQualityState.component.html'),
    controllerAs: 'vm',
    bindings: {

    }
};
